import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router-dom';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getMedicationReconciliationSummary } from '../HedisMetricService';
import MedicationReconciliationSummary from '../../../Models/HedisMetrics/MedicationReconciliationSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const MedicationReconciliationSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [medicationReconciliationSummary, setMedicationReconciliationSummary] = useState<MedicationReconciliationSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        medicationReconciliationHealthPlans,
        setMedicationReconciliationHealthPlans,
        medicationReconciliationTeam,
        setMedicationReconciliationTeam,
        medicationReconciliationDateFilter,
        setMedicationReconciliationDateFilter,
        medicationReconciliationFromDate,
        setMedicationReconciliationFromDate,
        medicationReconciliationToDate,
        setMedicationReconciliationToDate,
        medicationReconciliationIncludePastOpenStatus,
        setMedicationReconciliationIncludePastOpenStatus,
        medicationReconciliationShowInActiveTeam,
        setMedicationReconciliationShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'Medication Reconciliation Post Discharge',
                    'Documented vs Not Documented',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (
            medicationReconciliationDateFilter !== TimePeriodFilterEnum.Custom ||
            (Boolean(medicationReconciliationFromDate) && Boolean(medicationReconciliationToDate))
        ) {
            const changeFilter = async () => {
                let start = medicationReconciliationFromDate !== null ? new Date(medicationReconciliationFromDate).toDateString() : null;
                let end = medicationReconciliationToDate !== null ? new Date(medicationReconciliationToDate).toDateString() : null;
                const data = await getMedicationReconciliationSummary(
                    medicationReconciliationHealthPlans,
                    medicationReconciliationTeam,
                    medicationReconciliationDateFilter,
                    start,
                    end,
                    medicationReconciliationIncludePastOpenStatus,
                    medicationReconciliationShowInActiveTeam
                );
                setMedicationReconciliationSummary(data);
                setIsPrepared(true);
            };
            if (
                (medicationReconciliationFromDate !== null || medicationReconciliationToDate !== null) &&
                medicationReconciliationToDate < medicationReconciliationFromDate
            ) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Medication Reconciliation Post Discharge',
                        'Documented vs Not Documented',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        medicationReconciliationHealthPlans,
        medicationReconciliationTeam,
        medicationReconciliationDateFilter,
        medicationReconciliationFromDate,
        medicationReconciliationToDate,
        medicationReconciliationIncludePastOpenStatus,
        medicationReconciliationShowInActiveTeam,
    ]);

    useEffect(() => {
        if (medicationReconciliationSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: medicationReconciliationSummary.Documented,
                name: 'Documented',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Documented', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: medicationReconciliationSummary.NotDocumented,
                name: 'Not Documented',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Documented', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            if (medicationReconciliationSummary.Documented === 0 && medicationReconciliationSummary.NotDocumented === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'Medication Reconciliation Post Discharge',
                        'Documented vs Not Documented',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend('Medication Reconciliation Post Discharge', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight)
                );
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [medicationReconciliationSummary, medicationReconciliationHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(medicationReconciliationHealthPlans);
        setTeam(medicationReconciliationTeam);
        setFilter(medicationReconciliationDateFilter);
        setFDate(medicationReconciliationFromDate);
        setTDate(medicationReconciliationToDate);
        setIncludePastOpenStatus(medicationReconciliationIncludePastOpenStatus);
        setShowInActiveTeam(medicationReconciliationShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.MedicationReconciliation);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/MedicationReconciliation`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            Medication Reconciliation Post Discharge
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">Within 30 days of discharge from a facility</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Documented :
                                    </Typography>

                                    <Typography variant="caption">CPT code 1111F</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Documented :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={medicationReconciliationTeam}
                        setSelectedTeam={setMedicationReconciliationTeam}
                        dateFilter={medicationReconciliationDateFilter}
                        setDateFilter={setMedicationReconciliationDateFilter}
                        fromDate={medicationReconciliationFromDate}
                        setFromDate={setMedicationReconciliationFromDate}
                        toDate={medicationReconciliationToDate}
                        setToDate={setMedicationReconciliationToDate}
                        healthPlan={medicationReconciliationHealthPlans}
                        setHealthPlan={setMedicationReconciliationHealthPlans}
                        includePastOpenStatusMembers={medicationReconciliationIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setMedicationReconciliationIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.MedicationReconciliation}
                        showInActiveTeams={medicationReconciliationShowInActiveTeam}
                        setShowInActiveTeams={setMedicationReconciliationShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default MedicationReconciliationSummaries;
